var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable consistent-return */
/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable no-void */
import { initializeConnector } from '@web3-react/core';
import { Connector } from '@web3-react/types';
function isTally(provider) {
    return (typeof provider === 'object' &&
        provider !== null &&
        'request' in provider &&
        'isTally' in provider &&
        provider.isTally === true);
}
function isConnectEagerlyConfig(arg) {
    return (typeof arg === 'object' &&
        typeof arg.retries === 'number' &&
        typeof arg.timeoutMs === 'number');
}
function parseChainId(chainId) {
    return Number.parseInt(chainId, 16);
}
export class TallyHo extends Connector {
    /**
     * @param connectEagerly - A flag indicating whether connection should be initiated when the class is constructed.
     */
    constructor(actions, connectEagerly) {
        super(actions);
        this.isCurrentlyUsed = false;
        /**
         * This parameter is used to make sure only one initialization is in progress at a time.
         * E.g.: When `connectEagerly` constructor parameter set to true and the `tallyHo.connectEagerly()`
         * method is used in an useEffect then it results in 2 requests.
         */
        this.isActivationInProgress = false;
        if (connectEagerly) {
            if (connectEagerly === true) {
                void this.connectEagerly();
            }
            else if (isConnectEagerlyConfig(connectEagerly)) {
                void this.connectEagerly(connectEagerly);
            }
            else {
                throw new Error(`connectEagerly is expected to be 'true' OR {retries: number, timeoutMs: number}, but ${JSON.stringify(connectEagerly)} was received instead.`);
            }
        }
    }
    /** {@inheritdoc Connector.connectEagerly} */
    connectEagerly(config = { retries: 5, timeoutMs: 500 }) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                let counter = 0;
                /**
                 * When `connectEagerly` is set to true at class initialization time there is no window present.
                 * But it is usually ready in 1-2 seconds, so we shall wait for it.
                 */
                function waitForWindow() {
                    if (typeof window === 'undefined') {
                        counter++;
                        if (counter === config.retries) {
                            reject(`window was not present after ${config.retries} retries (with ${config.timeoutMs}ms wait period). Try using the connectEagerly method in a useEffect`);
                        }
                        setTimeout(waitForWindow, config.timeoutMs);
                    }
                    else {
                        resolve();
                    }
                }
                waitForWindow();
            }).then(() => this.activate());
        });
    }
    /** {@inheritdoc Connector.activate} */
    activate() {
        return __awaiter(this, void 0, void 0, function* () {
            if (window === undefined) {
                throw new Error("window is not defined. This should not have happened. 'Toto, I have a feeling we're not in Kansas anymore! 🌪'");
            }
            if (!this.provider) {
                this.initializeProvider();
            }
            if (this.isActivationInProgress)
                return;
            this.isActivationInProgress = true;
            if (isTally(this.provider)) {
                const cancelActivation = this.actions.startActivation();
                this.isCurrentlyUsed = true;
                return this.provider
                    .request({ method: 'eth_requestAccounts' })
                    .then((accounts) => {
                    var _a;
                    return Promise.all([
                        (_a = this.provider) === null || _a === void 0 ? void 0 : _a.request({
                            method: 'eth_chainId',
                        }),
                        accounts,
                    ]).then(([chainId, accounts]) => {
                        this.actions.update({ chainId: parseChainId(chainId), accounts });
                    });
                })
                    .catch((error) => {
                    this.actions.resetState();
                    cancelActivation();
                })
                    .finally(() => {
                    this.isActivationInProgress = false;
                });
            }
        });
    }
    initializeProvider() {
        if (!isTally(window.tally)) {
            throw new Error("You don't seem to have TallyHo installed because window.tally is not a TallyHo provider.");
        }
        this.provider = window.tally;
        this.provider.on('connect', ({ chainId }) => {
            this.actions.update({ chainId: parseChainId(chainId) });
        });
        this.provider.on('disconnect', (error) => {
            this.actions.update({ accounts: [], chainId: undefined });
            this.actions.resetState();
        });
        this.provider.on('chainChanged', (chainId) => {
            this.actions.update({ chainId: parseChainId(chainId) });
        });
        this.provider.on('accountsChanged', (accounts) => {
            if (!accounts.length) {
                this.actions.update({ accounts: [], chainId: undefined });
                this.actions.resetState();
            }
            this.actions.update({ accounts });
        });
    }
    deactivate() {
        if (this.provider) {
            this.provider = undefined;
            this.isCurrentlyUsed = false;
            this.actions.resetState();
        }
    }
}
export const createTallyHoConnector = () => {
    const [tallyHo, hooks] = initializeConnector((actions) => new TallyHo(actions, false));
    return { connector: tallyHo, hooks };
};
